import React, { useMemo, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useMembersTab } from './useMembersTab';
import WorkspaceHeader from './WorkspaceHeader';
import MembersList from './MembersList';
import InvitesList from './InvitesList';
import InviteMemberModal from './InviteMemberModal';
import ConfirmModal from './ConfirmModal';
import type { WorkspaceInvite, WorkspaceMember } from '../../../types';

const INVITE_LIMIT = 10;

const SkeletonRow: React.FC = () => (
  <div className="animate-pulse bg-white/5 h-12 rounded-lg" />
);

const MembersTab: React.FC = () => {
  const { user } = useAuth();
  const s = useMembersTab();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<WorkspaceMember | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<WorkspaceInvite | null>(null);

  const openInviteCount = useMemo(
    () => s.invites.filter((i) => i.status === 'open').length,
    [s.invites]
  );

  const memberBeingRemoved = confirmRemove;
  const inviteBeingRevoked = confirmRevoke;

  if (s.loading) {
    return (
      <div className="space-y-4">
        <SkeletonRow />
        <SkeletonRow />
        <SkeletonRow />
      </div>
    );
  }

  if (s.loadError) {
    return (
      <div className="px-4 py-3 rounded-lg bg-red-500/20 border border-red-400/40 text-red-100 text-sm flex items-center justify-between">
        <span>{s.loadError}</span>
        <button
          type="button"
          onClick={() => void s.refreshAll()}
          className="px-3 py-1 bg-red-500/30 hover:bg-red-500/50 rounded text-xs"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-4xl">
      {s.message && (
        <div
          className={`px-3 py-2 rounded-lg text-sm flex items-center justify-between ${
            s.message.type === 'success'
              ? 'bg-green-500/20 border border-green-400/40 text-green-100'
              : 'bg-red-500/20 border border-red-400/40 text-red-100'
          }`}
        >
          <span>{s.message.text}</span>
          <button
            type="button"
            onClick={() => s.setMessage(null)}
            className="ml-2 text-gray-300 hover:text-white text-lg leading-none"
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>
      )}

      <WorkspaceHeader
        workspace={s.workspace}
        canEdit={s.isAdmin}
        busy={s.busyId === 'workspace'}
        onRename={s.renameWorkspace}
      />

      {!s.isAdmin && (
        <div className="px-3 py-2 text-xs rounded-lg bg-white/5 border border-white/10 text-gray-300">
          You are a member of this workspace. Contact a workspace admin to change members or invites.
        </div>
      )}

      <MembersList
        members={s.members}
        currentUserId={user?.id}
        currentUserIsAdmin={s.isAdmin}
        activeAdminCount={s.activeAdminCount}
        busyId={s.busyId}
        onChangeRole={(userId, role) => void s.changeRole(userId, role)}
        onRemove={(member) => setConfirmRemove(member)}
      />

      <InvitesList
        invites={s.invites}
        filter={s.inviteFilter}
        canManage={s.isAdmin}
        busyId={s.busyId}
        openInviteCount={openInviteCount}
        inviteLimit={INVITE_LIMIT}
        onFilterChange={s.changeInviteFilter}
        onInviteClick={() => setInviteOpen(true)}
        onResend={(invite) => void s.resendInvite(invite.id)}
        onRevoke={(invite) => setConfirmRevoke(invite)}
      />

      {inviteOpen && (
        <InviteMemberModal
          onClose={() => setInviteOpen(false)}
          busy={s.busyId === 'new-invite'}
          onSubmit={s.createInvite}
        />
      )}

      {memberBeingRemoved && (
        <ConfirmModal
          title="Remove member?"
          confirmLabel="Remove member"
          busy={s.busyId === memberBeingRemoved.id}
          onClose={() => setConfirmRemove(null)}
          onConfirm={async () => {
            const ok = await s.removeMember(memberBeingRemoved.id);
            if (ok) setConfirmRemove(null);
          }}
          message={
            <>
              <p>
                <span className="font-semibold text-white">
                  {memberBeingRemoved.first_name || memberBeingRemoved.email}
                </span>{' '}
                will lose access to this workspace. This action is immediate — the user is disabled, not deleted.
              </p>
            </>
          }
        />
      )}

      {inviteBeingRevoked && (
        <ConfirmModal
          title="Revoke invite?"
          confirmLabel="Revoke invite"
          busy={s.busyId === inviteBeingRevoked.id}
          onClose={() => setConfirmRevoke(null)}
          onConfirm={async () => {
            const ok = await s.revokeInvite(inviteBeingRevoked.id);
            if (ok) setConfirmRevoke(null);
          }}
          message={
            <p>
              The invite link sent to{' '}
              <span className="font-semibold text-white">{inviteBeingRevoked.email}</span> will
              stop working immediately.
            </p>
          }
        />
      )}
    </div>
  );
};

export default MembersTab;
